import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import StatusToggle from '../ui/StatusToggle'

// Custom StatusBadge component (reuse from SatTestList/ActTestList)
const StatusBadge = ({ status }) => {
  if (status === 'None') {
    return (
      <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#E90004] font-medium whitespace-nowrap text-center leading-none w-[199px] px-5 py-2 border-r border-solid">
        <div className="bg-[rgba(233,0,4,0.10)] flex flex-col items-center justify-center px-2 py-1.5 rounded-[20px]">
          <div className="flex gap-1">
            <span className="flex items-center">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="6.5"
                  fill="#fff"
                  stroke="#E90004"
                  strokeWidth="1"
                />
                <path
                  d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
                  stroke="#E90004"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>Not Assigned</div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[rgba(4,146,185,1)] font-medium whitespace-nowrap text-center leading-none w-[199px] px-5 py-2 border-r border-solid">
      <div className="bg-[rgba(4,146,185,0.1)] flex flex-col items-center justify-center px-2 py-1.5 rounded-[20px]">
        <div className="flex gap-1">
          <img
            src="https://cdn.builder.io/api/v1/image/assets/d84e059b57274e6aae99b9cca7cfff8b/fa9cebe651f1f5ccad3b469ace21dc73ab8498b0?placeholderIfAbsent=true"
            className="aspect-[1] object-contain w-3.5 shrink-0"
            alt="Status icon"
          />
          <div>{status}</div>
        </div>
      </div>
    </div>
  )
}

function StudentList() {
  const [data, setData] = useState([])
  const [students, setStudents] = useState([])
  const { id } = useParams()
  const navigate = useNavigate()
  const [unassignModalOpen, setUnassignModalOpen] = useState(false)
  const [studentToUnassign, setStudentToUnassign] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getStudents(id)
  }, [])

  useEffect(() => {
    if (id) {
      getSingleStudent(id)
    }
  }, [id])

  const getSingleStudent = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}edit/${id}`
      )
      const studentData = response.data
      setData((prevData) =>
        prevData.map((student) =>
          student._id === studentData._id ? studentData : student
        )
      )
    } catch (error) {
      console.error(error)
    }
  }

  const getStudents = async (id) => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}getactivestudents/${id}`
      )
      if (response.status === 200) {
        setData(response.data)
      }
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const checkStudent = (itemId) => {
    let index = students.indexOf(itemId)
    let studentsTemp = students.slice()
    if (index > -1) {
      studentsTemp.splice(index, 1)
    } else {
      studentsTemp.push(itemId)
    }
    setStudents(studentsTemp)
  }

  const handleSubmitAssign = async (e) => {
    e.preventDefault()
    if (students.length === 0) {
      toast.error('Please choose student')
    } else {
      let dataSend = {
        batch_id: id,
        students: students,
      }
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BASE_URL}assignbatches`,
          dataSend,
          {
            withCredentials: true,
          }
        )
        if (data) {
          toast.success('Student Assigned Successfully.')
          window.location.reload(true)
        }
      } catch (error) {
        console.log(error)
      }
    }
  }

  const unassignStudent = async (studentId) => {
    const response = await axios.delete(
      `${process.env.REACT_APP_BASE_URL}unassignstudent/${studentId}/${id}`
    )
    toast.success(response.data)
    window.location.reload(true)
  }

  // Map data to reference structure for easier rendering
  const mappedData = data.map((item, index) => ({
    id: item._id,
    sNo: String(index + 1).padStart(2, '0'),
    name: `${item.first_name || ''} ${item.middle_name || ''} ${
      item.last_name || ''
    }`
      .replace(/ +/g, ' ')
      .trim(),
    email: item.email,
    school: item.school,
    phone: item.phone,
    status: item.status === 1 ? 'Assigned' : 'None',
    isActive: item.status === 1,
    isSelected: item.status === 1 || students.includes(item._id),
    raw: item,
  }))

  return (
    <div className="w-full mt-2.5 max-md:max-w-full">
      <div className="justify-between items-center border border-[color:var(--Stock,#F0F0F0)] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-3.5 rounded-[10px_10px_0px_0px] border-solid max-md:max-w-full">
        <div className="text-[#030303] text-lg self-stretch my-auto">
          Students List
        </div>
        <div className="flex gap-2">
          <button
            className="justify-center items-stretch flex flex-col text-xs text-white text-center w-[140px] bg-[#DE3C7D] my-auto p-2.5 rounded-md hover:bg-[#c72c6a] transition-colors font-medium"
            type="button"
            onClick={() => navigate('/batches')}
          >
            <div className="flex w-full gap-1">
              <div className="text-white">Back</div>
            </div>
          </button>
          <button
            className="justify-center items-stretch flex flex-col text-xs text-white text-center w-[140px] bg-[#DE3C7D] my-auto p-2.5 rounded-md hover:bg-[#c72c6a] transition-colors font-medium"
            type="submit"
            onClick={handleSubmitAssign}
          >
            <div className="flex w-full gap-1">
              <div className="text-white">Assign Students</div>
            </div>
          </button>
        </div>
      </div>
      {/* Table Header */}
      <div className="flex w-full bg-neutral-50 border-b border-l border-r border-[#F0F0F0]">
        <div className="w-20 flex-none px-4 py-3.5 border-r border-[#F0F0F0]"></div>
        {/* Checkbox */}
        <div className="w-20 flex-none px-4 py-3.5 border-r border-[#F0F0F0] text-center">
          S.No
        </div>
        <div className="w-60 flex-none px-4 py-3.5 border-r border-[#F0F0F0]">
          Student Name
        </div>
        <div className="w-80 flex-none px-4 py-3.5 border-r border-[#F0F0F0]">
          Email Address
        </div>
        <div className="w-72 flex-none px-4 py-3.5 border-r border-[#F0F0F0]">
          School
        </div>
        <div className="w-40 flex-none px-4 py-3.5 border-r border-[#F0F0F0]">
          Phone
        </div>
        <div className="w-60 flex-none px-4 py-3.5 border-r border-[#F0F0F0] text-center">
          Status
        </div>
        <div className="w-20 flex-none px-4 py-3.5 text-center">Action</div>
      </div>
      {/* Table Rows */}
      {loading ? (
        // Skeleton rows (5 rows)
        Array.from({ length: 5 }).map((_, idx) => (
          <div
            key={idx}
            className="flex w-full border-b border-l border-r border-[#F0F0F0] bg-white animate-pulse"
          >
            {[
              'w-20',
              'w-20',
              'w-60',
              'w-80',
              'w-72',
              'w-40',
              'w-60',
              'w-20',
            ].map((w, i) => (
              <div
                key={i}
                className={`${w} flex-none px-4 py-2 border-r border-[#F0F0F0] flex items-center justify-center`}
              >
                <div className="h-4 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ))
      ) : mappedData.length > 0 ? (
        mappedData.map((item, index) => (
          <div
            key={item.id}
            className={`flex w-full border-b border-l border-r border-[#F0F0F0] bg-white ${
              index === mappedData.length - 1 ? 'rounded-b-[10px]' : ''
            }`}
          >
            <div className="w-20 flex-none px-4 py-2 border-r border-[#F0F0F0] flex items-center justify-center">
              <label className="cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.isSelected}
                  onChange={() => checkStudent(item.id)}
                  className="sr-only"
                />
                <span className="inline-block w-4 h-4">
                  {item.isSelected ? (
                    <img
                      src="https://cdn.builder.io/api/v1/image/assets/d84e059b57274e6aae99b9cca7cfff8b/ef7fcf665d69a24f338a53eba4618a706e0d31d1?placeholderIfAbsent=true"
                      className="w-full h-full object-contain"
                      alt="Selected"
                    />
                  ) : (
                    <div className="w-full h-full border border-[color:var(--Text,#535862)] rounded-[3px] border-solid" />
                  )}
                </span>
              </label>
            </div>
            <div className="w-20 flex-none px-4 py-2 border-r border-[#F0F0F0] text-center">
              {item.sNo}
            </div>
            <div className="w-60 flex-none px-4 py-2 border-r border-[#F0F0F0]">
              {item.name}
            </div>
            <div className="w-80 flex-none px-4 py-2 border-r border-[#F0F0F0]">
              {item.email}
            </div>
            <div className="w-72 flex-none px-4 py-2 border-r border-[#F0F0F0]">
              {item.school}
            </div>
            <div className="w-40 flex-none px-4 py-2 border-r border-[#F0F0F0]">
              {item.phone}
            </div>
            <div className="w-60 flex-none px-4 py-2 border-r border-[#F0F0F0] flex items-center justify-center">
              <StatusBadge status={item.status} />
            </div>
            <div className="w-20 flex-none px-4 py-2 flex items-center justify-center">
              <StatusToggle
                status={item.isActive}
                onChange={
                  item.isActive
                    ? () => {
                        setStudentToUnassign(item.id)
                        setUnassignModalOpen(true)
                      }
                    : undefined
                }
                disabled={!item.isActive}
              />
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-[#535862] text-sm border-x border-b border-[color:var(--Stock,#F0F0F0)] bg-white rounded-b-[10px]">
          No Data Available
        </div>
      )}
      {/* Custom Unassign Modal */}
      {unassignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md shadow-lg">
            <h2 className="text-lg font-bold mb-4 text-center">
              Unassign Student
            </h2>
            <p className="mb-6 text-center text-[#535862]">
              Are you sure you want to unassign this student?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 rounded bg-[#DE3C7D] text-white font-semibold hover:bg-[#c72c6a] transition"
                onClick={async () => {
                  await unassignStudent(studentToUnassign)
                  setUnassignModalOpen(false)
                  setStudentToUnassign(null)
                }}
              >
                Yes, Unassign
              </button>
              <button
                className="px-6 py-2 rounded bg-gray-200 text-[#535862] font-semibold hover:bg-gray-300 transition"
                onClick={() => {
                  setUnassignModalOpen(false)
                  setStudentToUnassign(null)
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default StudentList
